import { format, parseISO } from "date-fns";
import { useFormikContext } from "formik";
import { FC, useMemo } from "react";
import {
	DataFieldType,
	DataFieldValues,
	DataFieldWithOptionContingentCheckResource,
	RuleGuestAttributeResourceTypeDataField,
	RuleResource,
	RuleResourceTypeGreaterThanEqual,
	RuleResourceTypeLessThanEqual,
	RuleTypes,
} from "../shared/types";
import { useRegistrationForm } from "./provider/registrationFormProviderContext";
import { RegistrationFormDataFieldBase } from "./RegistrationFormDataFieldBase";

interface Props {
	name: string;
	stepId: number;
	sectionId: number;
	dataField: DataFieldWithOptionContingentCheckResource;
	validationRules: RuleResource<RuleGuestAttributeResourceTypeDataField>[];
}

const getMinMaxRuleValue = (
	rule:
		| RuleResourceTypeGreaterThanEqual<RuleGuestAttributeResourceTypeDataField>
		| RuleResourceTypeLessThanEqual<RuleGuestAttributeResourceTypeDataField>
		| undefined,
	dataFieldValues: DataFieldValues,
) => {
	if (!rule) return "";
	return rule.guestAttributeReference ? dataFieldValues[rule.guestAttributeReference.dataField.id] : rule.value;
};

export const RegistrationDataField: FC<Props> = ({ name, dataField, validationRules }) => {
	const { values } = useFormikContext<DataFieldValues>();
	const { isPreview, registrationForm } = useRegistrationForm();
	const required = useMemo(
		() => !isPreview && validationRules.some((x) => x.type === RuleTypes.IS_REQUIRED),
		[isPreview, validationRules],
	);

	const readOnly = useMemo(() => {
		if (registrationForm.guest.id === "new") {
			return false;
		}
		if (validationRules.some((x) => x.type === RuleTypes.READ_ONLY)) {
			return true;
		}
		const rule = validationRules.find((x) => x.type === RuleTypes.NOT_UPDATABLE);
		if (rule) {
			const value = registrationForm.guest.guestAttributeValues.find(
				(value) => value.id === rule.guestAttribute.id,
			)?.value;
			if (Array.isArray(value)) {
				return value.length > 0;
			} else {
				return value !== null && value !== "" && value !== undefined;
			}
		}
		return false;
	}, [validationRules, registrationForm]);
	const minMaxProps = useMemo(() => {
		const ruleValueMin = getMinMaxRuleValue(
			validationRules.find((value) => value.type === RuleTypes.GREATER_THAN_EQUAL),
			values,
		);
		const ruleValueMax = getMinMaxRuleValue(
			validationRules.find((value) => value.type === RuleTypes.LESS_THAN_EQUAL),
			values,
		);

		if (dataField.type === DataFieldType.NUMBER) {
			return {
				min: ruleValueMin,
				max: ruleValueMax,
			};
		}
		if (DataFieldType.DATE === dataField.type) {
			return {
				min: ruleValueMin ? format(parseISO(ruleValueMin as string), "yyyy-MM-dd") : undefined,
				max: ruleValueMax ? format(parseISO(ruleValueMax as string), "yyyy-MM-dd") : undefined,
			};
		}
		if ([DataFieldType.DATETIME, DataFieldType.LOCAL_DATETIME].includes(dataField.type)) {
			return {
				min: ruleValueMin ? format(parseISO(ruleValueMin as string), "yyyy-MM-dd'T'HH:mm") : undefined,
				max: ruleValueMax ? format(parseISO(ruleValueMax as string), "yyyy-MM-dd'T'HH:mm") : undefined,
			};
		}
		return {};
	}, [validationRules, values, dataField.type]);

	return (
		<RegistrationFormDataFieldBase
			name={name}
			minMaxProps={minMaxProps}
			dataField={dataField}
			required={required}
			readOnly={readOnly}
		/>
	);
};
